import { faEllipsisV, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import AlertLineURLReviewerTable from "./AlertLineURLReviewerTable";
import {
  Container,
  BlockContainer,
  DocumentsContainer,
  DocumentContainer,
  ReviewersContainer,
  LogoContainer,
  LogoImageContainer,
  AvailableLanguageContainer,
  DocumentViewerContainer,
  AddNewLanguageContainer,
  SingleLanguageContainer,
  DeleteLanguageVersionButton,
  ActionsAndDocumentContainer,
  ActionsContainer,
} from "./styles";
import { AlertLineReviewerRef } from "../../models/alert-line-reviewer/alert-line-reviewer-ref";
import { AlertLineURLDTO } from "../../models/alert-line-url/alert-line-url-dto";

import { AlertLineURLLanguageVersionRef } from "../../models/alert-line-url/alert-line-url-language-version-ref";
import { AlertLineURLRef } from "../../models/alert-line-url/alert-line-url-ref";
import { AvailableLanguageDTO, AvailableLanguageRef } from "../../models/available-language";
import { CustomDate } from "../../models/custom-date/custom-date";
import { DocumentCategoryRef } from "../../models/document-categories";
import { DocumentTypeRef } from "../../models/document-types";
import { DocumentDTO, DocumentRef } from "../../models/documents";
import { Period, PeriodType } from "../../models/documents/period";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import DocumentViewer from "../../reusable-components/DocumentViewer/DocumentViewer";
import { IFile } from "../../reusable-components/DocumentViewer/interfaces";
import SimplePopover from "../../reusable-components/SimplePopover/SimplePopover";
import { AlertLineURLApiService } from "../../services/alert-line/alert-line-url-api.service";
import { AvailableLanguagesApiService } from "../../services/available-languages/available-languages-api.service";
import { AVAILABLE_LANGUAGES, DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from "../../utils/constants/constants";
import AddNewDocument from "../AddNewDocument/AddNewDocument";
import AttachedDocuments from "../AttachedDocuments/AttachedDocuments";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import ImageCropper from "../ImageCropper/ImageCropper";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

export interface CustomIFile extends IFile {
  fileId: string;
}
interface Props {
  effect: "edit" | "add";
  asLink?: boolean;
  linkText?: string;
  item?: AlertLineURLDTO;
  buttonVariant?: "text" | "icon" | "button";
  buttonText?: string;
  handleNotifyParent?: () => void;
  viewModeOnly?: boolean;
}

interface State {
  open: boolean;
  error: string;
  CCDOpen: boolean;
  loading: boolean;
  item: AlertLineURLDTO;
  selectedLanguageVersion: AlertLineURLLanguageVersionRef;
  docOneIFiles: CustomIFile[];
  docTwoIFiles: CustomIFile[];
  policyDocsIFiles: CustomIFile[];
  fileToView: IFile | null;
  newLanguage: AvailableLanguageDTO | null;
  addingNewLanguage: boolean;
  logo: Blob | null;
  logoURL: string;
  docOne: DocumentRef | null;
  docTwo: DocumentRef | null;
  policyDoc: DocumentRef | null;
}

const AddEditAlertLineURL = ({
  effect,
  item,
  handleNotifyParent,
  viewModeOnly,
  asLink = false,
  linkText,
  buttonVariant,
  buttonText,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.add_edit_alert_line_url";

  //#region default language version
  const DEFAULT_LANGUAGE_VERSION: AlertLineURLLanguageVersionRef = useMemo(
    () => ({
      isDeleted: false,
      alertLineURL: null,
      isDefault: false,
      id: "",
      language: AVAILABLE_LANGUAGES.ENGLISH as AvailableLanguageRef,
      description: "",
      files: [],
    }),
    []
  );
  //#endregion

  //#region default item
  const DEFAULT_ITEM: Partial<AlertLineURLDTO> = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      url: "",
      nickname: "",
      description: "",
      documentOne: null,
      documentTwo: null,
      policyDocument: null,
      validFrom: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
      },
      validTo: null,
      reviewers: [],
      logo: "",
      logoFileType: "",
      alertLineURLLanguageVersions: [
        {
          ...DEFAULT_LANGUAGE_VERSION,
          isDefault: true,
          id: uuid(),
        },
      ],
      files: [],
    }),
    [DEFAULT_LANGUAGE_VERSION]
  );
  //#endregion

  //#region initial state
  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      error: "",
      CCDOpen: false,
      loading: false,
      item: item ?? (DEFAULT_ITEM as AlertLineURLDTO),
      selectedLanguageVersion: item?.alertLineURLLanguageVersions?.find(
        (languageVersion) => languageVersion.isDefault
      ) ??
        item?.alertLineURLLanguageVersions[0] ??
        DEFAULT_ITEM.alertLineURLLanguageVersions?.[0] ?? { ...DEFAULT_LANGUAGE_VERSION, id: uuid() },
      docOneIFiles: [],
      docTwoIFiles: [],
      policyDocsIFiles: [],
      fileToView: null,
      newLanguage: null,
      addingNewLanguage: false,
      logo: null,
      logoURL: "",
      docOne: null,
      docTwo: null,
      policyDoc: null,
    }),
    [DEFAULT_ITEM, item, DEFAULT_LANGUAGE_VERSION]
  );
  //#endregion

  //#region set initial state
  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });
  //#endregion

  useEffect(() => {
    const docOne = state.selectedLanguageVersion?.files?.find((file) => file.isDocumentOne)?.document ?? null;
    const docTwo = state.selectedLanguageVersion?.files?.find((file) => file.isDocumentTwo)?.document ?? null;
    const policyDoc = state.selectedLanguageVersion?.files?.find((file) => file.isPolicyDoc)?.document ?? null;

    setState((state) => ({
      ...state,
      docOne,
      docTwo,
      policyDoc,
    }));
  }, [state.selectedLanguageVersion]);

  const handleChangeDoc = (docType: "docOne" | "docTwo" | "policyDoc", document: DocumentDTO | null) => {
    const files = [...state.selectedLanguageVersion.files];

    if (docType === "docOne") {
      const idx = files.findIndex((el) => el.isDocumentOne);

      if (idx > -1) {
        files.splice(idx, 1, { ...files[idx], document });
      } else {
        files.push({
          id: uuid(),
          isDeleted: false,
          alertLineURLLanguageVersion: state.selectedLanguageVersion,
          isPolicyDoc: false,
          isDocumentOne: true,
          isDocumentTwo: false,
          document,
        });
      }
    }

    if (docType === "docTwo") {
      const idx = files.findIndex((el) => el.isDocumentTwo);

      if (idx > -1) {
        files.splice(idx, 1, { ...files[idx], document });
      } else {
        files.push({
          id: uuid(),
          isDeleted: false,
          alertLineURLLanguageVersion: state.selectedLanguageVersion,
          isPolicyDoc: false,
          isDocumentOne: false,
          isDocumentTwo: true,
          document,
        });
      }
    }

    if (docType === "policyDoc") {
      const idx = files.findIndex((el) => el.isPolicyDoc);

      if (idx > -1) {
        files.splice(idx, 1, { ...files[idx], document });
      } else {
        files.push({
          id: uuid(),
          isDeleted: false,
          alertLineURLLanguageVersion: state.selectedLanguageVersion,
          isPolicyDoc: true,
          isDocumentOne: false,
          isDocumentTwo: false,
          document,
        });
      }
    }

    const langVersionIndex = state.item.alertLineURLLanguageVersions.findIndex(
      (item) => item.id === state.selectedLanguageVersion.id
    );
    const originalArray = state.item.alertLineURLLanguageVersions;

    originalArray.splice(langVersionIndex, 1, {
      ...state.selectedLanguageVersion,
      files,
    } as AlertLineURLLanguageVersionRef);

    setState((state) => ({
      ...state,
      selectedLanguageVersion: {
        ...state.selectedLanguageVersion,
        files,
      },
      item: {
        ...state.item,
        alertLineURLLanguageVersions: [...originalArray],
      },
    }));
  };

  //#region handle change
  const handleChange = useCallback((field: keyof AlertLineURLDTO, value: AlertLineURLDTO[keyof AlertLineURLDTO]) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        [field]: value,
      },
    }));
  }, []);
  //#endregion

  //#region change logo
  const handleChangeLogo = useCallback(async (image: Blob) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64string: string[] = (reader.result as string).split(",");

      const fileContent = base64string[1];
      const fileType = base64string[0].split(":")[1].split(";")[0];

      setState((state) => ({
        ...state,
        logo: image,
        logoURL: URL.createObjectURL(image),
        item: {
          ...state.item,
          logo: fileContent,
          logoFileType: fileType,
        },
      }));
    };
    reader.readAsDataURL(image);
  }, []);
  //#endregion

  //#region change reviewers
  const handleChangeReviewers = useCallback(
    (reviewer: AlertLineReviewerRef, removed?: boolean) => {
      const newListOfReviewers = [...state.item.reviewers.filter((_reviewer) => _reviewer.id !== reviewer.id)];

      if (!removed) newListOfReviewers.push({ ...reviewer });

      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          reviewers: [...newListOfReviewers],
        },
      }));
    },
    [state.item.reviewers]
  );
  //#endregion

  //#region save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const alertLineURLId = effect === "add" ? uuid() : state.item.id;
      const response = await new AlertLineURLApiService().saveItems([
        {
          ...state.item,
          alertLineURLLanguageVersions: [
            ...state.item.alertLineURLLanguageVersions.map((langVersion) => ({
              ...langVersion,
              alertLineURL: { id: alertLineURLId } as AlertLineURLRef,
            })),
          ],
          reviewers: [
            ...state.item.reviewers.map((reviewer) => ({
              ...reviewer,
              alertLineURL: { id: alertLineURLId } as AlertLineURLRef,
            })),
          ],
          ...(effect === "add" && {
            id: alertLineURLId,
          }),
        },
      ]);

      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        handleNotifyParent && handleNotifyParent();
        setState((state) => ({ ...state, open: false }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.item, handleNotifyParent, effect]);
  //#endregion

  //#region discard changes
  const handleDiscardChanges = useCallback(() => {
    setState({
      ...INITIAL_STATE,
    });
  }, [INITIAL_STATE]);
  //#endregion

  //#region change language version data
  const handleChangeLanguageVersionData = useCallback(
    (
      field: keyof AlertLineURLLanguageVersionRef,
      value: AlertLineURLLanguageVersionRef[keyof AlertLineURLLanguageVersionRef]
    ) => {
      const langVersionIndex = state.item.alertLineURLLanguageVersions.findIndex(
        (item) => item.id === state.selectedLanguageVersion.id
      );
      let originalArray = state.item.alertLineURLLanguageVersions;

      originalArray.splice(langVersionIndex, 1, {
        ...state.selectedLanguageVersion,
        [field]: value,
      } as AlertLineURLLanguageVersionRef);

      if (field === "isDefault") {
        originalArray = originalArray.map((languageVersion) =>
          languageVersion.id === state.selectedLanguageVersion.id
            ? languageVersion
            : {
                ...languageVersion,
                isDefault: false,
              }
        );
      }

      setState((state) => ({
        ...state,
        selectedLanguageVersion: {
          ...state.selectedLanguageVersion,
          [field]: value,
        } as AlertLineURLLanguageVersionRef,
        item: {
          ...state.item,
          alertLineURLLanguageVersions: [...originalArray],
        },
      }));
    },
    [state.item.alertLineURLLanguageVersions, state.selectedLanguageVersion]
  );
  //#endregion

  //#region add language version
  const addLanguageVersion = useCallback(() => {
    if (state.newLanguage) {
      const newLnaguageVersion: AlertLineURLLanguageVersionRef = {
        ...DEFAULT_LANGUAGE_VERSION,
        language: state.newLanguage,
        id: uuid(),
      };

      setState((state) => ({
        ...state,
        selectedLanguageVersion: newLnaguageVersion,
        newLanguage: null,
        addingNewLanguage: false,
        item: {
          ...state.item,
          alertLineURLLanguageVersions: [newLnaguageVersion, ...state.item.alertLineURLLanguageVersions],
        },
      }));
    }
  }, [DEFAULT_LANGUAGE_VERSION, state.newLanguage]);
  //#endregion

  //#region delete language version
  const deleteLanguageVersion = useCallback(
    (languagaVersion: AlertLineURLLanguageVersionRef) => {
      const confirm = window.confirm(`Are you sure you want to delete the ${languagaVersion.language?.name} version ?`);
      if (confirm) {
        let newList = state.item.alertLineURLLanguageVersions.filter(
          (languageVersion) => languageVersion.id !== languagaVersion.id
        );

        if (newList.length === 0) {
          newList.push({ ...DEFAULT_LANGUAGE_VERSION, isDefault: true, id: uuid() });
        }

        if (languagaVersion.isDefault) {
          newList = [{ ...newList[0], isDefault: true }, ...newList.splice(1)];
        }

        setState((state) => ({
          ...state,
          selectedLanguageVersion: newList[0],
          item: {
            ...state.item,
            alertLineURLLanguageVersions: [...newList],
          },
        }));
      }
    },
    [DEFAULT_LANGUAGE_VERSION, state.item.alertLineURLLanguageVersions]
  );
  //#endregion

  //#region load logo
  const loadAlertLineURLLogo = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new AlertLineURLApiService().getAlertLineURLLogo(state.item.id);
      if (response.size > 0) {
        handleChangeLogo(response);
      }
    } catch (error) {
      console.error("Error loading alert line URL logo:", error);
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.item.id]);

  //#endregion

  //#region load logo
  useEffect(() => {
    if (state.open) {
      loadAlertLineURLLogo();
    }
  }, [state.open]);
  //#endregion

  return (
    <>
      <AddEditButton
        effect={effect}
        title={`${t(effect === "add" ? `${tRP}.add_title` : `${tRP}.edit_title`)}`}
        onClick={() => setState((state) => ({ ...state, open: true }))}
        asLink={asLink}
        linkText={linkText}
        {...(buttonVariant === "text" && {
          buttonText: buttonText ? buttonText : effect === "edit" ? "Edit" : "Add new alert line URL",
        })}
      />
      <Dialog
        open={state.open}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          title={`${t(effect === "add" ? `${tRP}.add_title` : `${tRP}.edit_title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Container>
              <BlockContainer>
                <TextField
                  value={state.item.url}
                  label={`${t(`${tRP}.fields.url`)}`}
                  onChange={(e) => handleChange("url", e.target.value)}
                />
                <TextField
                  value={state.item.nickname}
                  label={`${t(`${tRP}.fields.nickname`)}`}
                  onChange={(e) => handleChange("nickname", e.target.value)}
                />
                <DesktopDatePicker
                  value={CustomDate.ToDate(state.item.validFrom)}
                  onChange={(newValue) =>
                    handleChange("validFrom", newValue ? CustomDate.FromDate(newValue) : newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t(`${tRP}.fields.valid_from`)}`}
                    />
                  )}
                />

                <DesktopDatePicker
                  value={CustomDate.ToDate(state.item.validTo)}
                  onChange={(newValue) => handleChange("validTo", newValue ? CustomDate.FromDate(newValue) : newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t(`${tRP}.fields.valid_to`)}`}
                    />
                  )}
                />

                <LogoContainer>
                  <Typography>{`${t(`${tRP}.fields.logo`)}`} :</Typography>
                  <LogoImageContainer>
                    {!!state.logo && (
                      <img
                        width="50"
                        src={state.logoURL}
                        alt={`${t(`${tRP}.fields.logo`)}`}
                      />
                    )}

                    <ImageCropper
                      propImageBlob={state.logo}
                      handleSaveImage={handleChangeLogo}
                      openButtonIcon={<FontAwesomeIcon icon={faEllipsisV} />}
                    />
                  </LogoImageContainer>
                </LogoContainer>
              </BlockContainer>

              <Divider
                flexItem
                orientation="vertical"
              />

              <Grid style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Grid style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Typography>Available languages : </Typography>
                  {/* <Grid style={{ display: "flex" , alignItems: "center", gap : "10px" }}>
                    {state.item.alertLineURLLanguageVersions.map((languageVersion) => (
                      <Button
                        variant={state.selectedLanguageVersion?.id === languageVersion.id ? "outlined" : "contained"}
                        onClick={() => setState((state) => ({ ...state, selectedLanguageVersion: languageVersion }))}

                      >
                        {languageVersion.language?.code}
                      </Button>
                    ))}
                  </Grid> */}
                </Grid>

                <AddNewLanguageContainer>
                  <Button
                    variant="contained"
                    onClick={() => setState((state) => ({ ...state, addingNewLanguage: true }))}
                    disabled={state.addingNewLanguage}
                  >
                    Add new language version
                  </Button>
                  {state.addingNewLanguage && (
                    <>
                      <AutocompleteDropdown
                        serviceApi={new AvailableLanguagesApiService()}
                        selectedValue={state.newLanguage}
                        field={"language"}
                        handleChange={(filed, value) => setState((state) => ({ ...state, newLanguage: value }))}
                        propsFilter={{
                          notIds: [
                            ...state.item.alertLineURLLanguageVersions.flatMap(
                              (languageVersion) => languageVersion.language?.id ?? ""
                            ),
                          ].filter((id) => !!id),
                        }}
                      />
                      <IconButton onClick={() => addLanguageVersion()}>
                        <FontAwesomeIcon
                          icon={faCheck}
                          color={theme.palette.success.main}
                        />
                      </IconButton>
                      <IconButton
                        onClick={() => setState((state) => ({ ...state, addingNewLanguage: false, newLanguage: null }))}
                      >
                        <FontAwesomeIcon
                          icon={faXmark}
                          color={theme.palette.error.main}
                        />
                      </IconButton>
                    </>
                  )}
                </AddNewLanguageContainer>
              </Grid>

              {/* <Typography>{state.selectedLanguage.code}</Typography> */}
              <AvailableLanguageContainer>
                {state.item.alertLineURLLanguageVersions.map((languageVersion) => (
                  <SingleLanguageContainer key={languageVersion.id}>
                    <Button
                      variant={state.selectedLanguageVersion?.id === languageVersion.id ? "contained" : "outlined"}
                      onClick={() => setState((state) => ({ ...state, selectedLanguageVersion: languageVersion }))}
                    >
                      {languageVersion.language?.name}
                      {languageVersion.isDefault && <Typography variant="body2">(Default)</Typography>}
                    </Button>
                  </SingleLanguageContainer>
                ))}
              </AvailableLanguageContainer>
              <BlockContainer>
                <TextField
                  value={state.selectedLanguageVersion?.description}
                  label={`${t("common_words.description")}`}
                  onChange={(e) => handleChangeLanguageVersionData("description", e.target.value)}
                  style={{ width: "100%" }}
                  minRows={17}
                  multiline
                />
                <ActionsAndDocumentContainer>
                  <ActionsContainer>
                    <Button
                      variant="contained"
                      disabled={state.selectedLanguageVersion.isDefault}
                      onClick={() => handleChangeLanguageVersionData("isDefault", true)}
                      style={{
                        backgroundColor: state.selectedLanguageVersion.isDefault
                          ? theme.palette.secondary.light
                          : theme.palette.actions.main,
                        color: theme.palette.primary.contrastText,
                      }}
                    >
                      Set as default language
                    </Button>
                    <DeleteLanguageVersionButton
                      onClick={() => deleteLanguageVersion(state.selectedLanguageVersion)}
                      sx={{ boxShadow: 2 }}
                      style={{
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.primary.contrastText,
                      }}
                    >
                      Delete language version
                      {/* <FontAwesomeIcon icon={faTrash} /> */}
                    </DeleteLanguageVersionButton>
                  </ActionsContainer>
                  <DocumentsContainer>
                    <DocumentContainer>
                      <Typography>{`${t(`${tRP}.fields.document_one`)}`} : </Typography>
                      <Grid style={{ display: "flex", alignItems: "center" }}>
                        <>
                          {!!state.docOne && (
                            <AttachedDocuments
                              documentIDs={[state.docOne.id]}
                              count={state.docOne.filesCount}
                              buttonSize="2x"
                              handleNotifyParentUponDelete={() => handleChangeDoc("docOne", null)}
                              closeAfterDocumentDelete
                              deleteDocumentIfEmpty
                              isGenericDocument
                              isWidget
                            />
                          )}

                          {!state.docOne && (
                            <AddNewDocument
                              buttonType="plusButton"
                              useMiniPlusButton
                              documentFieldsProps={{
                                category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
                                type: DOCUMENT_TYPES.Other as DocumentTypeRef,
                                period: Period.makePeriod(
                                  new Date().getFullYear(),
                                  PeriodType.Month,
                                  new Date().getMonth() + 1
                                ),
                                documentDate: new Date(),
                              }}
                              readOnlyFields={{
                                isPeriodRO: true,
                                isLegalEntityRO: true,
                                isTypeRO: true,
                                isCategoryRO: true,
                                isDateRO: true,
                              }}
                              hideReadOnlyFields
                              handleNotifyParent={(document) => {
                                handleChangeDoc("docOne", document);
                              }}
                              closeAfterSave
                              isGenericDocument
                            />
                          )}
                        </>
                      </Grid>
                    </DocumentContainer>
                    <DocumentContainer>
                      <Typography>{`${t(`${tRP}.fields.document_two`)}`} : </Typography>
                      <Grid style={{ display: "flex", alignItems: "center" }}>
                        {!!state.docTwo && (
                          <AttachedDocuments
                            documentIDs={[state.docTwo.id]}
                            count={state.docTwo.filesCount}
                            buttonSize="2x"
                            handleNotifyParentUponDelete={() => handleChangeDoc("docTwo", null)}
                            closeAfterDocumentDelete
                            deleteDocumentIfEmpty
                            isGenericDocument
                            isWidget
                          />
                        )}

                        {!state.docTwo && (
                          <AddNewDocument
                            buttonType="plusButton"
                            useMiniPlusButton
                            documentFieldsProps={{
                              category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
                              type: DOCUMENT_TYPES.Other as DocumentTypeRef,
                              period: Period.makePeriod(
                                new Date().getFullYear(),
                                PeriodType.Month,
                                new Date().getMonth() + 1
                              ),
                              documentDate: new Date(),
                            }}
                            readOnlyFields={{
                              isPeriodRO: true,
                              isLegalEntityRO: true,
                              isTypeRO: true,
                              isCategoryRO: true,
                              isDateRO: true,
                            }}
                            hideReadOnlyFields
                            handleNotifyParent={(document) => {
                              handleChangeDoc("docTwo", document);
                            }}
                            closeAfterSave
                            isGenericDocument
                          />
                        )}
                      </Grid>
                    </DocumentContainer>
                    <DocumentContainer>
                      <Typography>{`${t(`${tRP}.fields.policy_document`)}`} : </Typography>
                      <Grid style={{ display: "flex", alignItems: "center" }}>
                        {!!state.policyDoc && (
                          <AttachedDocuments
                            documentIDs={[state.policyDoc.id]}
                            count={state.policyDoc.filesCount}
                            buttonSize="2x"
                            handleNotifyParentUponDelete={() => handleChangeDoc("policyDoc", null)}
                            closeAfterDocumentDelete
                            deleteDocumentIfEmpty
                            isGenericDocument
                            isWidget
                          />
                        )}

                        {!state.policyDoc && (
                          <AddNewDocument
                            buttonType="plusButton"
                            useMiniPlusButton
                            documentFieldsProps={{
                              category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
                              type: DOCUMENT_TYPES.Other as DocumentTypeRef,
                              period: Period.makePeriod(
                                new Date().getFullYear(),
                                PeriodType.Month,
                                new Date().getMonth() + 1
                              ),
                              documentDate: new Date(),
                            }}
                            readOnlyFields={{
                              isPeriodRO: true,
                              isLegalEntityRO: true,
                              isTypeRO: true,
                              isCategoryRO: true,
                              isDateRO: true,
                            }}
                            hideReadOnlyFields
                            handleNotifyParent={(document) => {
                              handleChangeDoc("policyDoc", document);
                            }}
                            closeAfterSave
                            isGenericDocument
                          />
                        )}
                      </Grid>
                    </DocumentContainer>

                    <SimplePopover
                      buttonType="text"
                      buttonContent="How to add links to documents in description ?"
                      buttonVariant="contained"
                      customButtonStyle={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.primary.main,
                        width: "fit-content",
                        textTransform: "none",
                      }}
                    >
                      <Grid>
                        We have three documents : <b>document one</b> , <b>document two</b> and <b>policy document</b>
                        <br />
                        <br />
                        Example : <br />
                        In the description we want to make a link to :
                        <br />
                        <br />
                        <b>document one</b> with text <b>&quot;Check this document&quot;</b> <br />
                        <b>document two</b> with text <b>&quot;Check other document&quot;</b>
                        <br />
                        <b>policy document</b> with text <b>&quot;Review policy&quot;</b>
                        <br />
                        <br />
                        at the desired positions in the text add : <br />
                        <br />
                        &#123;&#123;documentOne: Check this document &#125;&#125;
                        <br />
                        &#123;&#123;documentTwo: Check other document &#125;&#125;
                        <br />
                        &#123;&#123;policyDocument: Review policy &#125;&#125;
                        <br />
                        <br />
                        The system will automatically replace that text with a link to the corresponding document
                        uploaded
                      </Grid>
                    </SimplePopover>
                  </DocumentsContainer>
                </ActionsAndDocumentContainer>
              </BlockContainer>
            </Container>
            <Container>
              {/* <BlockContainer style={{ width: "100%" }}> */}
              <Divider
                orientation="horizontal"
                style={{
                  marginBottom: "20px",
                }}
              >
                <Chip
                  label="Reviewers"
                  style={{
                    color: theme.palette.secondary.light,
                  }}
                />
              </Divider>

              <ReviewersContainer>
                <>
                  <AlertLineURLReviewerTable
                    reviewers={state.item.reviewers}
                    handleUpdateParent={handleChangeReviewers}
                    alertLineURL={state.item}
                  />
                </>
              </ReviewersContainer>
              {/* </BlockContainer> */}
            </Container>
          </LoadingOverlay>
        </DialogContent>

        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={handleSave}
          disabledSaveButton={state.loading}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={handleDiscardChanges}
      />
      <DocumentViewerContainer open={!!state.fileToView}>
        <DialogTitle
          title=""
          handleClose={() => {
            setState((state) => ({ ...state, fileToView: null }));
          }}
        />
        <Grid style={{ width: "90vw" }}>
          {state.fileToView && (
            <DocumentViewer
              file={state.fileToView}
              withFooter
            />
          )}
        </Grid>
      </DocumentViewerContainer>
    </>
  );
};
export default AddEditAlertLineURL;
